/**
 * Where a freshly-registered member lands.
 *
 * Signup used to navigate unconditionally to `/dashboard`, which is right for
 * someone who came to join and wrong for someone who came to BUY: a visitor
 * who clicked "Enrol now" on a campaign page for a specific course was dropped
 * on an activation-kit grid with no trace of the thing they wanted, and had to
 * find it again themselves. `?buy=<productId>` carries that intent through
 * signup so the funnel ends at payment, not at a dashboard.
 *
 * Precedence — `buy` beats `next` beats the dashboard. `buy` wins because it is
 * only ever set by a funnel entry (`/buy/:productId`), which is a narrower and
 * more recent statement of intent than a generic return path.
 */

/** Only an internal, single-leading-slash path. Blocks `//evil.com` and absolute
 *  URLs being used as an open redirect. Mirrors `safeNext` in login.tsx — the
 *  two must stay identical; this is the shared copy new callers should use. */
export function safeInternalPath(path: string | null | undefined): string | null {
  return path && /^\/(?!\/)/.test(path) ? path : null;
}

/** A UUID, the only shape a product id ever takes. Anything else is discarded
 *  rather than pasted into a URL — `?buy=` arrives from a query string. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function resolvePostSignupDestination(params: {
  /** `?buy=<productId>` — the product this signup was a step toward buying. */
  buy?: string | null;
  /** `?next=<path>` — a generic internal return path. */
  next?: string | null;
}): string {
  const buy = params.buy?.trim();
  if (buy && UUID_RE.test(buy)) return `/buy/${buy}`;
  return safeInternalPath(params.next) ?? '/dashboard';
}
